/**
 * Product recommendations. Loaded as type="module".
 * Fetches Shopify recommendations and wires a native scroll-snap carousel.
 */

const SELECTOR = '[data-section-type="recommended-products"]'

function initCarousel(node, options = {}) {
  const viewport = node.classList.contains('swiper') ? node : node.querySelector('.swiper')
  if (!viewport) return

  const track = viewport.querySelector('.swiper-wrapper') || viewport
  const slides = [...track.children]
  const nextButton = node.querySelector('[data-next]')
  const prevButton = node.querySelector('[data-prev]')
  const breakpoints = Object.keys(options.breakpoints || {})
    .map(Number)
    .sort((a, b) => a - b)

  track.classList.add('flex', 'snap-x', 'snap-mandatory', 'overflow-x-auto', 'scroll-smooth')
  viewport.classList.add('overflow-hidden')
  slides.forEach((slide) => slide.classList.add('snap-start', 'shrink-0'))

  function config() {
    let next = { slidesPerView: options.slidesPerView, spaceBetween: options.spaceBetween }
    for (const bp of breakpoints) {
      if (window.innerWidth >= bp) next = { ...next, ...options.breakpoints[bp] }
    }
    return next
  }

  function sync() {
    if (!track.isConnected) {
      window.removeEventListener('resize', sync)
      return
    }

    const { slidesPerView, spaceBetween } = config()
    if (slidesPerView) {
      const percent = `${100 / slidesPerView}%`
      slides.forEach((slide) => {
        slide.style.width = percent
      })
    }
    if (spaceBetween != null) track.style.gap = `${spaceBetween}px`

    const hasOverflow = track.scrollWidth > track.clientWidth + 1
    const atStart = track.scrollLeft <= 1
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1

    viewport.classList.toggle('has-overflow', hasOverflow)
    prevButton?.classList.toggle('swiper-button-disabled', atStart)
    nextButton?.classList.toggle('swiper-button-disabled', atEnd)
    prevButton?.classList.toggle('swiper-button-lock', !hasOverflow)
    nextButton?.classList.toggle('swiper-button-lock', !hasOverflow)
  }

  nextButton?.addEventListener('click', () => {
    track.scrollBy({ left: viewport.clientWidth * 0.8, behavior: 'smooth' })
  })
  prevButton?.addEventListener('click', () => {
    track.scrollBy({ left: -viewport.clientWidth * 0.8, behavior: 'smooth' })
  })
  track.addEventListener('scroll', sync, { passive: true })
  track.addEventListener('focusin', (event) => {
    if (!document.body.classList.contains('user-is-tabbing')) return
    event.target.closest('.swiper-slide')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })
  })
  window.addEventListener('resize', sync)
  sync()
}

async function init(container) {
  if (container.dataset.recommendationsInit) return
  container.dataset.recommendationsInit = 'true'

  const content = container.querySelector('[data-recommendations]')
  if (!content) return

  const { limit, productId, sectionId, productsPerView, mobileProductsPerView } = container.dataset
  const perView = parseInt(productsPerView, 10)
  const route = window.theme?.routes?.productRecommendations || '/recommendations/products'

  try {
    const html = await fetch(
      `${route}?section_id=${sectionId}&limit=${limit}&product_id=${productId}`
    ).then((response) => response.text())

    const wrapper = document.createElement('div')
    wrapper.innerHTML = html
    const recommendations = wrapper.querySelector('[data-recommendations]')
    if (!recommendations?.querySelector('.carousel__slide')) {
      container.remove()
      return
    }

    content.innerHTML = recommendations.innerHTML
    window.OneskinProductItem?.(container)
    initCarousel(content, {
      slidesPerView: parseInt(mobileProductsPerView, 10) * 1.07,
      spaceBetween: 6,
      breakpoints: {
        720: { slidesPerView: perView === 5 ? perView - 1 : perView },
        1200: { slidesPerView: perView },
      },
    })
  } catch {
    container.remove()
  }
}

function initAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach(init)
}

initAll()

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target)
})
